import { BaseRepository } from '../BaseRepository';
import { BFF_ENDPOINTS, type BffEndpoint } from '../../endpoints/BffEndpoints';
import type {
  BeneficiariesListResponse,
  BeneficiaryResponse,
  PatchBeneficiaryPayload,
  PatchBeneficiaryPersonPayload,
  CreateBeneficiaryPayload,
} from '../../models/bff/Beneficiary';
import type { PatchBeneficiaryPersonInput, CreateBeneficiaryInput } from '../../models/Beneficiary';
import { BffAuthenticatedApiClient } from '../../clients/BffAuthenticatedApiClient';
import type { HttpResponse } from '../../http/HttpClient';

const NAME = 'beneficiaries';

/**
 * BeneficiariesRepository class for handling beneficiaries-related API operations.
 */
export class BeneficiariesRepository extends BaseRepository<BffEndpoint> {
  static override readonly name = NAME;

  constructor() {
    super(BffAuthenticatedApiClient.getInstance());
  }

  getBeneficiaries(subscriptionId: string): Promise<HttpResponse<BeneficiariesListResponse>> {
    return this.apiClient.get<BeneficiariesListResponse>(
      BFF_ENDPOINTS.SUBSCRIPTION_BENEFICIARIES,
      {
        pathParams: {
          subscriptionId,
        },
      },
    );
  }

  patchBeneficiary(
    subscriptionId: string,
    beneficiaryId: string,
    relationship?: string | null,
    person?: PatchBeneficiaryPersonInput | null,
  ): Promise<HttpResponse<BeneficiaryResponse>> {
    return this.apiClient.patch<BeneficiaryResponse, PatchBeneficiaryPayload>(
      BFF_ENDPOINTS.SUBSCRIPTION_BENEFICIARY,
      {
        relationship,
        person: this.mapPatchPersonPayload(person),
      },
      {
        pathParams: {
          subscriptionId,
          beneficiaryId,
        },
      },
    );
  }

  deleteBeneficiary(subscriptionId: string, beneficiaryId: string): Promise<HttpResponse<void>> {
    return this.apiClient.delete<void>(
      BFF_ENDPOINTS.SUBSCRIPTION_BENEFICIARY,
      {
        pathParams: {
          subscriptionId,
          beneficiaryId,
        },
      },
    );
  }

  createBeneficiary(subscriptionId: string, input: CreateBeneficiaryInput): Promise<HttpResponse<BeneficiaryResponse>> {
    return this.apiClient.post<BeneficiaryResponse, CreateBeneficiaryPayload>(
      BFF_ENDPOINTS.SUBSCRIPTION_BENEFICIARIES,
      {
        relationship: input.relationship,
        person: {
          first_name: input.person.firstName,
          last_name: input.person.lastName,
          salutation: input.person.salutation,
          date_of_birth: input.person.dateOfBirth,
          gender: input.person.gender,
          nationality: input.person.nationality,
        },
      },
      {
        pathParams: {
          subscriptionId,
        },
      },
    );
  }

  private mapPatchPersonPayload(person?: PatchBeneficiaryPersonInput | null): PatchBeneficiaryPersonPayload | null | undefined {
    if (person == null) {
      return person;
    }

    return {
      nationality: person.nationality,
      country: person.country,
      currency: person.currency,
      locale: person.locale,
      travel_docs: person.travelDocs == null
        ? undefined
        : person.travelDocs?.map((travelDoc) => ({
          type: travelDoc.type,
          number: travelDoc.number,
          country: travelDoc.country,
          expiration_date: travelDoc.expirationDate,
        })),
      phones: person.phones,
      addresses: person.addresses == null
        ? undefined
        : person.addresses?.map((address) => ({
          type: address.type,
          street: address.street,
          apartment: address.apartment,
          city: address.city,
          state: address.state,
          postal_code: address.postalCode,
          country: address.country,
        })),
    };
  }
}

declare module '../Repositories' {
  export interface Repositories {
    [NAME]: BeneficiariesRepository;
  }
}